/**
 * NPC ambient animation (q-npc-ambient-animations, owner-directed 2026-10-18): what a placed
 * rig NPC does with its body. Before this, every NPC played the player's one idle loop
 * (de-phased). Now each NPC plays a set chosen from content:
 *
 *   - a LOOP picked per NPC from its set's pool (so a crowd never moves as one), which is a
 *     calm idle for townsfolk and a work loop for the trades (a lantern held aloft, a basket
 *     being filled, a mug, a herald calling out, a mystic seated on the ground);
 *   - occasional BEATS over the loop (a smith's hammer stroke, a merchant's hand-rub);
 *   - a GREETING once as the player walks up, then a TALK loop while the player stands in
 *     talk range, turning to face them; back to the loop (and the post's facing) after.
 *
 * Everything here is client-side presentation (AD-026: the server owns position; nothing
 * rides the wire). Proximity is read from the own avatar's rendered position, which the
 * client already has; the NPC's own (x, z) is the join ack's.
 *
 * DATA, not logic (Principle 2): `NPC_AMBIENT_TABLE` is the set → clip table and
 * `ambientSetFor` the role default; content picks a set per NPC with the optional
 * `ambient` field (`NPC_AMBIENT_SETS`, validated in shared). The clips ship in the
 * clips-only `player-rig-v1/npc-ambient.glb` (tools/rig/npc-ambient; sources + trims in
 * docs/art/npc-ambient-handover.json). A clip the delivered rig lacks is skipped, and with
 * nothing usable the NPC falls back to the rig's own idle — exactly the pre-change look.
 *
 * Node-safe: the mixer is the injected `RigMixerHandle`, so the whole driver runs under
 * `node --test` with a recording fake (AD-120). Deterministic per NPC id (FNV-1a + a
 * seeded mulberry32 for beat timing — never `Math.random`), so a capture reproduces.
 */

import { NPC_AMBIENT_SETS, type NpcAmbientSet } from '@great-cloud/shared/client';

import { resolveClipFor, type RigClip, type RigMixerHandle } from '../rig-avatar/index.js';

/** What one ambient set plays. Clip names are exact (the pack's own names). */
export interface NpcAmbientSpec {
  /** Looping clips; ONE is picked per NPC (deterministically by id). */
  loops: readonly string[];
  /** One-shots played now and then over the loop while nobody is near. */
  beats: readonly string[];
  /** Seconds between beats, `[min, max]` (each gap drawn per NPC, deterministically). */
  beatEverySeconds: readonly [number, number];
  /** One-shot greetings; one picked per NPC; played as the player walks up. */
  greet: readonly string[];
  /** Talk loops; one picked per NPC; played while the player stands in talk range. */
  talk: readonly string[];
  /** Whether the NPC turns to face a player who walks up (a seated mystic stays put). */
  facesPlayer: boolean;
}

/** The body-base idle (`player-rig-v1`): one of the calm variants, and the old look. */
const RIG_IDLE = 'Idle_3';
const CALM = [
  RIG_IDLE,
  'Npc_Idle_Breathe',
  'Npc_Idle_Rest',
  'Npc_Idle_Lean',
  'Npc_Idle_Listen',
] as const;

/**
 * The set → clip table. Picks reviewed on stick-figure sheets + the visual lane
 * (q-npc-ambient-animations evidence); the owner's taste call on any of them is a
 * one-line change here, never code.
 */
export const NPC_AMBIENT_TABLE: Readonly<Record<NpcAmbientSet, NpcAmbientSpec>> = {
  townsfolk: {
    loops: CALM,
    beats: ['Npc_Beat_Ponder'],
    beatEverySeconds: [14, 30],
    greet: ['Npc_Greet_Wave', 'Npc_Greet_WaveBig'],
    talk: ['Npc_Talk_Explain', 'Npc_Talk_Open', 'Npc_Talk_HandOnHip'],
    facesPlayer: true,
  },
  merchant: {
    loops: [RIG_IDLE, 'Npc_Idle_Breathe', 'Npc_Idle_Lean'],
    beats: ['Npc_Beat_HandRub', 'Npc_Beat_Offer'],
    beatEverySeconds: [8, 18],
    greet: ['Npc_Greet_Wave', 'Npc_Greet_Bow'],
    talk: ['Npc_Talk_HandOnHip', 'Npc_Talk_Explain'],
    facesPlayer: true,
  },
  scholar: {
    loops: ['Npc_Idle_Listen', 'Npc_Idle_Rest', 'Npc_Idle_Breathe'],
    beats: ['Npc_Beat_Ponder'],
    beatEverySeconds: [8, 16],
    greet: ['Npc_Greet_Bow'],
    talk: ['Npc_Talk_Explain', 'Npc_Talk_Open'],
    facesPlayer: true,
  },
  smith: {
    loops: [RIG_IDLE, 'Npc_Idle_Breathe'],
    beats: ['Npc_Beat_Hammer'],
    beatEverySeconds: [2.5, 5],
    greet: ['Npc_Greet_Wave'],
    talk: ['Npc_Talk_HandOnHip'],
    facesPlayer: true,
  },
  guard: {
    loops: ['Npc_Work_Watch'],
    beats: [],
    beatEverySeconds: [0, 0],
    greet: ['Npc_Greet_Wave'],
    talk: ['Npc_Talk_HandOnHip', 'Npc_Talk_Explain'],
    facesPlayer: true,
  },
  herald: {
    loops: ['Npc_Work_Call'],
    beats: [],
    beatEverySeconds: [0, 0],
    greet: ['Npc_Greet_Flourish', 'Npc_Greet_WaveBig'],
    talk: ['Npc_Talk_Passionate'],
    facesPlayer: true,
  },
  forager: {
    loops: ['Npc_Work_Basket'],
    beats: [],
    beatEverySeconds: [0, 0],
    greet: ['Npc_Greet_Wave'],
    talk: ['Npc_Talk_Open', 'Npc_Talk_Explain'],
    facesPlayer: true,
  },
  lantern: {
    loops: ['Npc_Work_Torch'],
    beats: [],
    beatEverySeconds: [0, 0],
    greet: ['Npc_Greet_Wave'],
    talk: ['Npc_Talk_Explain'],
    facesPlayer: true,
  },
  tavern: {
    loops: ['Npc_Work_Drink'],
    beats: [],
    beatEverySeconds: [0, 0],
    greet: ['Npc_Greet_WaveBig'],
    talk: ['Npc_Talk_Passionate', 'Npc_Talk_Open'],
    facesPlayer: true,
  },
  mystic: {
    loops: ['Npc_Work_SitFloor'],
    beats: [],
    beatEverySeconds: [0, 0],
    greet: [],
    talk: [],
    facesPlayer: false,
  },
  performer: {
    loops: ['Npc_Talk_Passionate', 'Npc_Idle_Listen'],
    beats: [],
    beatEverySeconds: [0, 0],
    greet: ['Npc_Greet_Flourish'],
    talk: ['Npc_Talk_Passionate', 'Npc_Talk_Open'],
    facesPlayer: true,
  },
  // On the stool in NPC_AMBIENT_PROPS.seated (q-npc-props-seated-clips). Every greeting and
  // talk clip in the pack is a standing one, so a seated NPC keeps its seat, like the mystic.
  seated: {
    loops: ['Npc_Sit_Chair'],
    beats: [],
    beatEverySeconds: [0, 0],
    greet: [],
    talk: [],
    facesPlayer: false,
  },
};

/** Role defaults, first match wins (an NPC with no `ambient` in content). */
export const NPC_AMBIENT_ROLE_DEFAULTS: readonly (readonly [string, NpcAmbientSet])[] = [
  ['vendor', 'merchant'],
];
/** The set for an NPC with no `ambient` and no defaulted role. */
export const NPC_AMBIENT_FALLBACK: NpcAmbientSet = 'townsfolk';

/** The set an NPC plays: its content `ambient` if valid, else the role default. */
export function ambientSetFor(def: { ambient?: string; roles?: readonly string[] }): NpcAmbientSet {
  const named = def.ambient;
  if (named !== undefined && (NPC_AMBIENT_SETS as readonly string[]).includes(named)) {
    return named as NpcAmbientSet;
  }
  for (const [role, set] of NPC_AMBIENT_ROLE_DEFAULTS) {
    if (def.roles?.includes(role) === true) return set;
  }
  return NPC_AMBIENT_FALLBACK;
}

/** Walk-up distance (m) at which an NPC greets and starts turning to the player. */
export const NPC_GREET_RANGE_M = 8;
/** Beyond this (m) the NPC lets the player go: faces its post again and re-arms its greeting. */
export const NPC_ENGAGE_RELEASE_M = 11;
/**
 * Talk loop range (m): the HUD's "[E] Talk" prompt range (ui/panels/nearby.ts
 * `NPC_TALK_PROMPT_RANGE_M`), so the NPC gestures exactly while it can be talked to.
 */
export const NPC_TALK_RANGE_M = 5.5;
/** The talk loop ends once the player is this far away (m) — hysteresis against flicker. */
export const NPC_TALK_RELEASE_M = 7;
/** Cross-fade for ambient transitions (s) — slower than combat's 0.18 s; nothing here is urgent. */
export const NPC_AMBIENT_FADE_SECONDS = 0.35;
/** Turn rates (rad/s): toward an approaching player, and back to the post afterwards. */
export const NPC_TURN_TO_PLAYER_RAD_PER_S = 3;
export const NPC_TURN_HOME_RAD_PER_S = 1.2;

/** FNV-1a (32-bit) over a string — the placer's idle-stagger hash. */
export function fnv1a(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

/** Deterministic pick from a non-empty pool by `npcId` + a slot label. */
function pick(pool: readonly string[], npcId: string, slot: string): string | null {
  if (pool.length === 0) return null;
  return pool[fnv1a(`${npcId}:${slot}`) % pool.length] ?? null;
}

/** mulberry32 — a tiny seeded PRNG in [0, 1) (beat timing; never `Math.random`). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The resolved per-NPC plan: concrete clips this rig carries (or `null` where none). */
export interface NpcAmbientPlan {
  set: NpcAmbientSet;
  /** The loop this NPC plays at rest — never null in a plan (no loop ⇒ no plan). */
  loop: string;
  beats: readonly string[];
  beatEverySeconds: readonly [number, number];
  greet: string | null;
  talk: string | null;
  facesPlayer: boolean;
}

/**
 * Resolve an NPC's set against the clips its rig actually carries. Clips the rig lacks
 * are dropped; with no usable loop the rig's own idle (`resolveClipFor('idle')`) stands
 * in — the pre-change look. `null` when the rig has no idle either (it stays static).
 */
export function planNpcAmbient(
  npcId: string,
  set: NpcAmbientSet,
  clipNames: readonly string[],
): NpcAmbientPlan | null {
  const spec = NPC_AMBIENT_TABLE[set];
  const have = new Set(clipNames);
  const usable = (pool: readonly string[]): string[] => pool.filter((name) => have.has(name));
  const loop = pick(usable(spec.loops), npcId, 'loop') ?? resolveClipFor('idle', clipNames);
  if (loop === null) return null;
  return {
    set,
    loop,
    beats: usable(spec.beats),
    beatEverySeconds: spec.beatEverySeconds,
    greet: pick(usable(spec.greet), npcId, 'greet'),
    talk: pick(usable(spec.talk), npcId, 'talk'),
    facesPlayer: spec.facesPlayer,
  };
}

/** Where the observer (the own avatar) stands relative to this NPC, or null if unknown. */
export interface NpcObserver {
  x: number;
  z: number;
}

/** What the driver is presenting (tests + `__gcSceneDump`). */
export type NpcAmbientMode = 'loop' | 'beat' | 'greet' | 'talk';

/** Shortest signed angle from `from` to `to`, radians, in (-π, π]. */
function angleDelta(from: number, to: number): number {
  let d = (to - from) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  if (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

/**
 * Drives ONE placed NPC's mixer from its plan + the observer's position. Construct with the
 * mixer, call `start(phaseSeconds)` once, then `update(dtSeconds, observer)` per frame (it
 * advances the mixer itself). `yaw` is the facing the placer writes to the NPC root.
 * `addClips` takes a late clip pack onto the same mixer and re-plans in place.
 */
export class NpcAmbientDriver {
  private current: NpcAmbientPlan;
  private readonly npcId: string;
  private readonly mixer: RigMixerHandle;
  private readonly x: number;
  private readonly z: number;
  private readonly homeYaw: number;
  private readonly random: () => number;
  private mode: NpcAmbientMode = 'loop';
  /** The looping clip currently shown (the loop or the talk). */
  private shown: string | null = null;
  /** A one-shot is playing (beat/greet): the loop/talk choice waits for it. */
  private busy = false;
  /** Greeting armed: true until it fires, re-armed once the player leaves. */
  private greetArmed = true;
  /** The player is within the engage radius (turning toward them; beats paused). */
  private engaged = false;
  private talking = false;
  /**
   * A rest loop kept on screen after `addClips` re-planned to a different loop: it stays
   * until the next natural transition (a beat, a greeting, the talk loop), then `plan.loop`.
   * Set only for a set with beats, whose beat clock ends the hold on its own.
   */
  private heldLoop: string | null = null;
  private untilBeat: number;
  /** Current facing (rad about +Y; 0 = the rig's authored forward, +Z). */
  yaw: number;

  constructor(options: {
    npcId: string;
    plan: NpcAmbientPlan;
    mixer: RigMixerHandle;
    x: number;
    z: number;
    homeYaw?: number;
  }) {
    this.current = options.plan;
    this.npcId = options.npcId;
    this.mixer = options.mixer;
    this.x = options.x;
    this.z = options.z;
    this.homeYaw = options.homeYaw ?? 0;
    this.yaw = this.homeYaw;
    this.random = mulberry32(fnv1a(`${options.npcId}:beats`));
    this.untilBeat = this.nextBeatGap();
  }

  /** The plan in force (replaced by `addClips`). */
  get plan(): NpcAmbientPlan {
    return this.current;
  }

  /** What is showing now (tests + diagnostics). */
  get currentMode(): NpcAmbientMode {
    return this.mode;
  }

  /** The looping clip on screen now, or null while a one-shot plays (tests + diagnostics). */
  get shownLoop(): string | null {
    return this.shown;
  }

  /**
   * A late clip pack landed (q-npc-pack-clips-without-restart; the low tier places NPCs on
   * the body's clips first). The clips join this driver's LIVE mixer and the plan is
   * re-resolved against `clipNames` (every clip the rig now carries). Nothing restarts:
   * no new mixer, no stagger, no `play`. The loop on screen keeps its clip and its time,
   * and the beat clock, greeting and talk state carry over.
   *
   * The new set takes effect from the next natural transition, and the NPC always reaches
   * its set's own per-id loop (the look a desktop NPC has from the first frame):
   *   - a set with BEATS keeps the loop on screen until its first beat (the beat clock
   *     starts at the landing, so within the set's longest gap), and the set's loop
   *     follows that beat; a greeting or the talk loop ends the hold sooner;
   *   - a set with no beats would hold until a player happened to walk up (a work loop, a
   *     seat), so for it the landing is the transition: the next `update` cross-fades
   *     (NPC_AMBIENT_FADE_SECONDS, same mixer) to its loop. A pose change, not a restart.
   *
   * Throws only if the mixer's `addClips` throws, and then nothing here has changed.
   * Returns `false` (nothing changed) when the merged clips give no plan.
   */
  addClips(clips: readonly RigClip[], clipNames: readonly string[]): boolean {
    const resting = !this.busy && this.mode === 'loop' ? this.shown : null;
    const next = planNpcAmbient(this.npcId, this.current.set, clipNames);
    if (next === null) return false;
    // Before the plan: a plan naming clips the mixer lacks would play no-op one-shots that
    // never finish, and the driver would wait on them forever.
    this.mixer.addClips(clips);
    this.current = next;
    const beatEndsHold = next.beats.length > 0;
    this.heldLoop = resting !== null && resting !== next.loop && beatEndsHold ? resting : null;
    return true;
  }

  /** Start the loop, de-phased by `phaseSeconds` (the placer's per-id stagger). */
  start(phaseSeconds: number): void {
    this.showLoop(this.current.loop, 0);
    if (phaseSeconds > 0) this.mixer.update(phaseSeconds);
  }

  /**
   * One frame: decide greet / talk / beat / loop from the observer's distance, turn toward
   * (or away from) the observer, then advance the mixer by `dtSeconds`.
   */
  update(dtSeconds: number, observer: NpcObserver | null): void {
    const distance =
      observer === null
        ? Number.POSITIVE_INFINITY
        : Math.hypot(observer.x - this.x, observer.z - this.z);

    // Engage / release with hysteresis; the greeting re-arms only once the player has left.
    if (!this.engaged && distance <= NPC_GREET_RANGE_M) this.engaged = true;
    else if (this.engaged && distance > NPC_ENGAGE_RELEASE_M) {
      this.engaged = false;
      this.greetArmed = true;
    }
    if (!this.talking && distance <= NPC_TALK_RANGE_M) this.talking = true;
    else if (this.talking && distance > NPC_TALK_RELEASE_M) this.talking = false;

    const plan = this.current;
    if (this.engaged && this.greetArmed && !this.busy && plan.greet !== null) {
      this.greetArmed = false;
      this.playOneShot(plan.greet, 'greet');
    } else if (this.engaged && this.greetArmed && plan.greet === null) {
      this.greetArmed = false; // a set with no greeting just engages
    }

    if (!this.busy) {
      const want = this.wantedLoop();
      if (want !== this.shown) this.showLoop(want, NPC_AMBIENT_FADE_SECONDS);
      if (!this.engaged && plan.beats.length > 0) {
        this.untilBeat -= dtSeconds;
        if (this.untilBeat <= 0) {
          const beat = plan.beats[Math.floor(this.random() * plan.beats.length)];
          this.untilBeat = this.nextBeatGap();
          if (beat !== undefined) this.playOneShot(beat, 'beat');
        }
      }
    }

    this.turn(dtSeconds, observer);
    this.mixer.update(dtSeconds);
  }

  /** Stop the mixer (the placer detaches the NPC). */
  stop(): void {
    this.mixer.stop();
  }

  private nextBeatGap(): number {
    const [min, max] = this.current.beatEverySeconds;
    return min + this.random() * Math.max(0, max - min);
  }

  /** The looping clip to show now: the talk loop in talk range, else the rest loop. */
  private wantedLoop(): string {
    if (this.talking && this.current.talk !== null) {
      this.heldLoop = null; // talking is a natural transition
      return this.current.talk;
    }
    return this.heldLoop ?? this.current.loop;
  }

  private showLoop(clip: string, fade: number): void {
    this.mixer.play(clip, fade);
    this.shown = clip;
    this.mode = clip === this.current.loop || clip === this.heldLoop ? 'loop' : 'talk';
  }

  private playOneShot(clip: string, mode: NpcAmbientMode): void {
    this.busy = true;
    this.mode = mode;
    this.shown = null; // the loop/talk is re-chosen (and re-faded in) when this ends
    this.heldLoop = null; // a one-shot is a natural transition: the plan's loop follows it
    this.mixer.playOnce(clip, NPC_AMBIENT_FADE_SECONDS, {
      onFinished: () => {
        this.busy = false;
        this.showLoop(this.wantedLoop(), NPC_AMBIENT_FADE_SECONDS);
      },
    });
  }

  private turn(dtSeconds: number, observer: NpcObserver | null): void {
    if (!this.current.facesPlayer) return;
    const toPlayer =
      this.engaged && observer !== null
        ? Math.atan2(observer.x - this.x, observer.z - this.z)
        : null;
    const target = toPlayer ?? this.homeYaw;
    const rate = toPlayer !== null ? NPC_TURN_TO_PLAYER_RAD_PER_S : NPC_TURN_HOME_RAD_PER_S;
    const delta = angleDelta(this.yaw, target);
    const step = rate * dtSeconds;
    this.yaw = Math.abs(delta) <= step ? target : this.yaw + Math.sign(delta) * step;
  }
}

/** The clip names a rig's clip list carries (helper for the placer). */
export function clipNamesOf(clips: readonly RigClip[]): string[] {
  return clips.map((clip) => clip.name);
}
